import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * A simplified cart gift bag component that handles dialog interaction and add-to-cart.
 * Cart eligibility is handled in Liquid, so this component only appears when eligible.
 */
class CartGiftBagComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {boolean} Is currently adding to cart */
  #isAdding = false;

  connectedCallback() {
    try {
      super.connectedCallback();

      // Use event delegation to handle clicks
      this.addEventListener('click', this.#handleComponentClick);
    } catch (error) {
      console.error('Error in cart gift bag connectedCallback:', error);
      console.error('Error stack:', error.stack);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleComponentClick);
  }

  /**
   * Handle all clicks within the component using event delegation
   * @param {Event} event
   */
  #handleComponentClick = (event) => {
    try {
      const target = /** @type {HTMLElement} */ (event.target);
      if (!target) return;

      // Find the actual button element (in case user clicked on child elements)
      const button = target.closest('button');
      if (!button) return;

      const ref = button.getAttribute('ref');

      switch (ref) {
        case 'showDialogButton':
          this.#handleShowDialog();
          break;
        case 'addButton':
          this.#handleAddToCart(event);
          break;
        case 'closeButton':
        case 'cancelButton':
          this.closeDialog();
          break;
      }
    } catch (error) {
      console.error('Error in cart gift bag click handler:', error);
    }
  };

  /**
   * Handle showing the dialog
   */
  #handleShowDialog = () => {
    this.showDialog();
  };

  /**
   * Handle add to cart button click
   * @param {Event} event
   */
  #handleAddToCart = async (event) => {
    event.preventDefault();

    if (this.#isAdding) return;

    // Get the add button from the event target
    const target = /** @type {HTMLElement} */ (event.target);
    const addButton = /** @type {HTMLButtonElement} */ (target?.closest('button[ref="addButton"]'));
    const variantId = addButton?.dataset.variantId;

    if (!variantId) {
      console.error('No variant ID found for gift bag');
      return;
    }

    this.#isAdding = true;
    this.#setButtonState('adding');

    try {
      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }]
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const responseData = await response.json();

      if (responseData.errors) {
        throw new Error(responseData.errors);
      }

      this.#setButtonState('added');

      // Dispatch cart add event
      document.dispatchEvent(
        new CartAddEvent(responseData, 'cart-gift-bag', {
          itemCount: responseData.item_count || 0,
          source: 'cart-gift-bag'
        })
      );

      // Close dialog after short delay
      setTimeout(() => {
        this.closeDialog();
        this.#setButtonState('default');
        this.#isAdding = false;
      }, 1000);
    } catch (error) {
      console.error('Error adding gift bag to cart:', error);
      this.#setButtonState('error');

      setTimeout(() => {
        this.#setButtonState('default');
        this.#isAdding = false;
      }, 2000);
    }
  };

  /**
   * Set button state with appropriate text and disabled state
   * @param {string} state - The button state (default, adding, added, error)
   */
  #setButtonState(state) {
    const addButton = /** @type {HTMLButtonElement} */ (this.querySelector('[ref="addButton"]'));

    if (!addButton) {
      return;
    }

    const text = addButton.dataset[`${state}Text`];

    if (text) {
      addButton.textContent = text;
    }

    addButton.disabled = state === 'adding';
  }
}

if (!customElements.get('cart-gift-bag-component')) {
  customElements.define('cart-gift-bag-component', CartGiftBagComponent);
}
